/* KANON `Programy v2 - D3 (0.8)` — the muscle split, transcribed.
 *
 * Read off the rendered specimen at 1512x1000, with the FIGURES grown a third by owner decision
 * D24 of 4 September 2026 -- the canon's placeholder body was too small to read as anatomy:
 *   section  0 19 padding, an 8px column, 172.5 tall including its heading (canon: 137.5)
 *   body     17.5 between the figures and the readout, the readout centred against the figures
 *   figures  two 76.5-wide columns, 9.5 apart, each a 4px stack of SVG over caption (canon: 59)
 *   svg      76.5 x 138.5 (canon: 59 x 106.5)
 *   caption  12 / 500, sentence case, --text-3 (owner, 4 November 2026)
 *   readout  the canon's 9.5px bar over its legend. It is a RANKING since 16 September 2026
 *            (owner) — see `MuscleRanking` — and since 4 November 2026 five rows of real groups
 *            with the remainder as one 12 / 500 line under them
 *
 * ── The SECTION's reserve follows the figure, and it has to ──────────────────────────────────
 * `--h-pl-muscle` is a fixed height and `.body` is `flex: 1` inside it, so a figure column taller
 * than the box would overflow the card rather than push it. The token grew by exactly what the
 * figure column gained -- the 32px of SVG plus the caption's taller line -- which is also what
 * keeps the readout's `align-self: center` honest, because the space it centres in changed.
 *
 * ── The readout is CENTRED, not stacked from the top ─────────────────────────────────────────
 * The specimen puts the readout's block at exactly the vertical middle of the figures beside it, so
 * the two read as one object rather than as a caption under a drawing. `align-self: center` on a
 * block whose own height is its content's.
 *
 * ── The whole section reserves `--h-pl-muscle` ───────────────────────────────────────────────
 * Held from the first frame, so the strip below it never moves. Every state — the wait, an empty
 * split, five rows and their remainder — stands inside that one box.
 *
 * **Every text leaf states its line-height.** `base.css` puts `--lh-relaxed` on `body` and the
 * specimen's body carries none, so an unstated leaf renders about 2px taller than the canon.
 *
 * Heights and widths are layout rather than design tokens (see stylelint.config.js); every colour,
 * space, radius and type value below is a token. */

.section {
  display: flex;
  flex: none;
  flex-direction: column;
  gap: var(--space-2);
  height: var(--h-pl-muscle);
  padding: var(--space-0) var(--space-19px);
}

.body {
  display: flex;
  flex: 1;
  gap: var(--space-17-5px);
  min-height: 0;
}

/* ── The pair of figures ────────────────────────────────────────────────────────────────────── */

.figures {
  display: flex;
  flex: none;
  gap: var(--space-9-5px);
}

/* `--h-silhouette` is the contract `BodySilhouette` reads for its own box; the width is this
 * surface's. Between them they give the specimen's 59 x 106.5 footprint. */
.figureBox {
  --h-silhouette: var(--h-pl-figure);

  /* NO ring on this figure. `BodySilhouette` draws one by default and its header says why; the
   * short of it is that the ring is there to rescue a categorical palette whose fills cannot be
   * chosen for contrast against each other. The four `--silhouette-*` steps ARE chosen for it, so
   * here the ring only competes: at 0.38 white it is brighter than the `mid` step, which outlined
   * every muscle at the strength of a worked one.
   *
   * The value arrives as a custom property because Stylelint's allowlist takes a token reference
   * and nothing else, and `transparent` is not one — a custom property is never read by that gate. */
  --silhouette-ring: transparent;

  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  align-items: center;
  width: var(--w-pl-figure);
}

.figureCaption {
  font-size: var(--type-label);
  font-weight: var(--fw-medium);
  line-height: var(--type-label-lh);
  color: var(--text-3);
}

/* ── The bar, its legend, and what stands there instead ─────────────────────────────────────── */

/* `min-height` is the RESERVE, and it is what `align-self: center` makes load-bearing: a centred
 * box's top is `(space − its own height) / 2`, so every pixel the readout gained as it filled
 * lifted the bar by half of it. Measured on a cold entry with `/full` delayed 400ms: the bar and
 * the legend stood 14.4px lower before the skeleton appeared and 1px lower again while it was up.
 * The reserve is the TALLEST loaded shape — five rows and the remainder line — and the content
 * stacks from the box's top, so a plan with no remainder, the skeleton and the empty sentence all
 * stand inside the same box at the same place. */
.readout {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-2);
  align-self: center;
  min-width: 0;
  min-height: var(--h-pl-readout);
}

/* The five rows leave this much out. A note under the ranking, quieter than any row in it. */
.remainder {
  margin: var(--space-0);
  font-size: var(--type-label);
  font-weight: var(--fw-medium);
  line-height: var(--type-label-lh);
  color: var(--text-3);
}

/* A statement of fact, in the section's own neutral ink — a plan with no catalogued exercises has
 * nothing to draw, and nothing has gone wrong. */
.empty {
  margin: var(--space-0);
  font-size: var(--fs-pl-body);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-3);
}

/* ── The wait ───────────────────────────────────────────────────────────────────────────────── */

/* The ranking's own column, at its own pitch, so the bars stand where the rows will. */
.skelRanking {
  display: flex;
  flex-direction: column;
  gap: var(--space-5-5px);
}

/* One ranked row's line, `--type-control-lh`, which is the row's whole height (the track beside it
 * is shorter). The bar inside is thinner than the line so the block reads as a list. */
.skelSlot {
  display: flex;
  align-items: center;
  height: var(--type-control-lh);
}

/* The widths taper so the block reads as a list rather than as a grid. */
.skelRow {
  height: 11px;
}

.skelSlot:nth-child(1) .skelRow { width: 92%; }
.skelSlot:nth-child(2) .skelRow { width: 88%; }
.skelSlot:nth-child(3) .skelRow { width: 84%; }
.skelSlot:nth-child(4) .skelRow { width: 80%; }
.skelSlot:nth-child(5) .skelRow { width: 76%; }
